import React, { useState, useEffect, useRef } from 'react';
import TermsContent from '../components/TermsContent';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { 
  CheckCircle, 
  Wifi, 
  Trash2, 
  LogOut, 
  ChevronRight, 
  ChevronLeft,
  Lock,
  MessageCircle,
  AlertTriangle,
  X,
  Info,
  KeyRound
} from 'lucide-react';

const API_BASE = "https://qwipsvc.up.railway.app";

// --- API Helper ---
const apiFetch = async (url: string, options: RequestInit = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    throw new Error("API request failed");
  }
  
  return response.json();
};

// --- Error Box Component ---
const ErrorBox = () => (
  <div className="flex flex-col items-center justify-center flex-1 px-6">
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center max-w-sm">
      <p className="text-red-800 font-medium">
        Error – contact support for more details.
      </p>
    </div>
  </div>
);

// --- Video Modal Component ---
const VideoModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="relative w-[95vw] max-w-md bg-black rounded-2xl overflow-hidden"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="aspect-[9/16] max-h-[80vh]">
          <iframe
            src="https://www.youtube.com/embed/j7D2JxN2Fso?autoplay=1"
            title="How to use the pod"
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </motion.div>
    </motion.div>
  );
};

// --- Back Button Component ---
const BackButton = ({ onClick }: { onClick: () => void }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors text-sm font-medium"
  >
    <ChevronLeft className="w-4 h-4" />
    Back
  </button>
);

// --- Step 0: Finalizing Payment ---
const FinalizingPayment = () => {
  return (
    <motion.div 
      className="flex flex-col items-center justify-center flex-1 text-center px-8"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
      >
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
          <CheckCircle className="w-10 h-10 text-primary" strokeWidth={1.5} />
        </div>
      </motion.div>
      
      <motion.h2 
        className="font-heading text-2xl text-primary font-bold mb-2"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        Payment Authorised
      </motion.h2>
      
      <motion.p 
        className="text-muted-foreground"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        Securing your sanctuary...
      </motion.p>
      
      <motion.div 
        className="mt-8 flex gap-1"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
      >
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-2 h-2 rounded-full bg-primary"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
          />
        ))}
      </motion.div>
    </motion.div>
  );
};

// --- Guide Card Component ---
const GuideCard = ({ 
  icon: Icon, 
  title, 
  desc, 
  action 
}: { 
  icon: React.ElementType; 
  title: string; 
  desc: string; 
  action?: React.ReactNode;
}) => (
  <motion.div
    className="bg-white rounded-2xl p-5 shadow-sm border border-[#153F36]/10"
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
  >
    <div className="flex flex-col gap-4">
      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
        <Icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
      </div>
      <h3 className="font-heading text-xl font-bold text-foreground">{title}</h3>
      <p className="text-muted-foreground text-base leading-relaxed whitespace-pre-line">{desc}</p>
    </div>
    {action && <div className="mt-5">{action}</div>}
  </motion.div>
);

// --- Step 1: Getting Started Guide ---
const OnboardingGuide = ({ onComplete, onBack }: { onComplete: () => void; onBack: () => void }) => {
  const [cardIndex, setCardIndex] = useState(0);
  

  // Reset scroll position when guide opens (iOS fix)
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);

  const cards = [
    {
      icon: Wifi,
      title: "WiFi Access",
      desc: "Enjoy high-speed Wi-Fi from Westfield. Connect to '_Free Westfield Wi-Fi', then follow the instructions on the splash page to get online.",
    },
    {
      icon: Trash2,
      title: "Keep it clean",
      desc: "No food or open drink containers inside the pod (water/coffee with a lid is ok!)\n\nPlease take any litter with you when leaving. A clean pod is a happy pod.",
    },
    {
      icon: LogOut,
      title: "Ending session",
      desc: "Important: You must use the link sent to your email, or the booking page that you will be redirected to, to end this booking.",
      action: (
        <div className="flex items-start gap-2 bg-amber-50 rounded-lg px-3 py-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <span className="text-amber-700">
            Failure to click the link in your email will result in ongoing charges.
          </span>
        </div>
      )
    },
    {
      icon: MessageCircle,
      title: "Need help?",
      desc: "Having trouble finding your pod or accessing the WiFi? Drop us a message directly on WhatsApp.",
      action: (
        <a 
          href="https://chat.whatsapp.com/Lx9H5OJpywmCa022EcliOC" 
          target="_blank" 
          rel="noopener noreferrer"
          className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl px-4 py-3 text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          <MessageCircle className="w-4 h-4" />
          WhatsApp Support
        </a>
      )
    },
    {
      icon: KeyRound,
      title: "Entering your code",
      desc: "When you get your access code, tap the circular button on the handle to wake the keypad.\n\nOnce the keypad lights up, type in your access code and press # to unlock.\n\nThe circle will glow green to confirm the door is unlocked.",
    }
  ];

  const handleNext = () => {
    if (cardIndex < cards.length - 1) {
      setCardIndex(prev => prev + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (cardIndex > 0) {
      setCardIndex(prev => prev - 1);
    } else {
      onBack();
    }
  };

  const handleDragEnd = (_: unknown, info: { offset: { x: number } }) => {
    if (info.offset.x < -50) {
      handleNext();
    } else if (info.offset.x > 50) {
      handleBack();
    }
  };

  const showBackButton = cardIndex > 0;

  return (
    <motion.div 
      className="flex flex-col flex-1 overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="flex-1 overflow-auto px-4 pt-6">
        {showBackButton && (
          <div className="mb-4">
            <BackButton onClick={handleBack} />
          </div>
        )}
        
        <h2 className="font-heading text-xl font-bold text-foreground text-center mb-4">Quick Guide</h2>
        
        {/* Info Banner */}
        <div className="bg-primary/10 rounded-xl px-4 py-3 mb-4 flex items-start gap-2">
          <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <p className="text-sm text-primary">
            Your session charges will only begin once you complete the steps below.
          </p>
        </div>
        
        <motion.div
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.2}
          onDragEnd={handleDragEnd}
          className="touch-pan-y"
        >
          <AnimatePresence mode="wait">
            <GuideCard key={cardIndex} {...cards[cardIndex]} />
          </AnimatePresence>
        </motion.div>
        
        <div className="flex justify-center gap-2 mt-8 pb-4">
          {cards.map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all duration-300 ${
                i === cardIndex ? 'w-5 h-2 bg-primary' : 'w-2 h-2 bg-primary/25'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="flex-shrink-0 px-4 pb-4 pt-2 bg-[#f9f8f5]">
        <button
          onClick={handleNext}
          className="w-full bg-primary text-primary-foreground rounded-2xl py-4 font-medium text-base flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors"
        >
          {cardIndex === cards.length - 1 ? "I'm Ready" : "Next"}
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

// --- Step 2: Terms & Conditions ---
const TermsAndConditions = ({ onComplete, onBack, isLoading }: { onComplete: () => void; onBack: () => void; isLoading: boolean }) => {
  const [canAccept, setCanAccept] = useState(false);
  const [showHint, setShowHint] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      setCanAccept(true);
      setShowHint(false);
    }
  };

  const handleAccept = () => {
    if (canAccept && !isLoading) {
      onComplete();
    } else if (!canAccept) {
      setShowHint(true);
    }
  };

  return (
    <motion.div 
      className="flex flex-col flex-1 overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="flex-shrink-0 px-4 pt-4">
        <div className="mb-4">
          <BackButton onClick={onBack} />
        </div>
        <h2 className="font-heading text-xl font-bold text-foreground mb-4">Terms of Service</h2>
      </div>

      <div 
        className="flex-1 overflow-auto px-4"
        onScroll={handleScroll}
      >
        <div className="text-sm pb-6">
          <TermsContent />
        </div>
      </div>

      <div className="flex-shrink-0 px-4 pb-4 pt-2 bg-[#f9f8f5] space-y-3">
        <AnimatePresence>
          {showHint && (
            <motion.p 
              className="text-amber-600 text-sm text-center"
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              Please scroll to the bottom to continue
            </motion.p>
          )}
        </AnimatePresence>
        
        <button
          onClick={handleAccept}
          disabled={isLoading}
          className={`w-full rounded-xl py-3.5 font-medium transition-all ${
            canAccept && !isLoading
              ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          }`}
        >
          {isLoading ? "Processing..." : "Accept & Continue"}
        </button>
      </div>
    </motion.div>
  );
};

// --- Step 3: Generating Access Code ---
const GeneratingAccessCode = () => {
  return (
    <motion.div
      className="flex flex-col items-center justify-center flex-1 text-center px-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.3 }}
    >
      <div className="relative mb-8">
        <motion.div
          className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Lock className="w-8 h-8 text-primary" strokeWidth={1.5} />
        </motion.div>
      </div>
      
      <h2 className="font-heading text-xl text-primary font-bold mb-2">
        Generating Access Code...
      </h2>
      <p className="text-muted-foreground text-sm">
        Connecting to secure lock
      </p>
      
      <motion.div 
        className="mt-8 flex gap-1"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-2 h-2 rounded-full bg-primary"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
          />
        ))}
      </motion.div>
    </motion.div>
  );
};

// --- Step 4: Access Code Reveal ---
const RevealDigit = ({ digit, delay }: { digit: string; delay: number }) => {
  return (
    <motion.div
      className="flex items-center justify-center"
      initial={{ opacity: 0, y: 30, scale: 0.8, filter: "blur(10px)" }}
      animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
      transition={{ 
        delay, 
        duration: 0.6, 
        ease: [0.16, 1, 0.3, 1],
        filter: { duration: 0.4 }
      }}
    >
      <motion.span 
        className="font-heading text-5xl text-primary font-bold"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: delay + 0.2, duration: 0.3 }}
      >
        {digit}
      </motion.span>
    </motion.div>
  );
};

const AccessCodeReveal = ({ accessCode, sessionToken }: { accessCode: string; sessionToken: string }) => {
  const [showVideo, setShowVideo] = useState(false);
  const digits = accessCode.split('');

  return (
    <div className="flex flex-col flex-1 px-6 pt-4 pb-6">
      <div className="flex-1 flex flex-col items-center justify-center">
        <motion.div
          className="flex flex-col items-center text-center w-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className="flex flex-col items-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
              >
                <CheckCircle className="w-8 h-8 text-primary" strokeWidth={1.5} />
              </motion.div>
            </div>
            <h2 className="font-heading text-2xl text-primary font-bold">Access Granted</h2>
            <p className="text-muted-foreground">You're all set</p>
          </motion.div>

          <motion.div
            className="w-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex justify-center gap-4 mb-4">
              {digits.map((digit, index) => (
                <RevealDigit key={index} digit={digit} delay={0.5 + index * 0.15} />
              ))}
              <RevealDigit digit="#" delay={0.5 + digits.length * 0.15} />
            </div>
            
            <motion.p 
              className="text-muted-foreground text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3 }}
            >
              Access Code
            </motion.p>
          </motion.div>

          <motion.div
            className="w-full mt-8 space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
          >
            <a
              href={`https://qwip.co.uk/session?t=${sessionToken}`}
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl py-3.5 font-medium hover:bg-primary/90 transition-colors"
            >
              Manage Your Session
            </a>
            
          </motion.div>
        </motion.div>
      </div>

      <AnimatePresence>
        {showVideo && <VideoModal isOpen={showVideo} onClose={() => setShowVideo(false)} />}
      </AnimatePresence>
    </div>
  );
};

// --- Main Page Component ---
// Provisioning statuses: 'pending' | 'ready' | 'failed'

export default function Session() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t');
  
  const [step, setStep] = useState(0); // 0: finalizing, 1: guide, 2: terms, 3: generating, 4: access code
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [accessCode, setAccessCode] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  
  const pollingRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Check for token on mount
  useEffect(() => {
    if (!token) {
      setError(true);
      return;
    }

    // Call finalize API
    const finalize = async () => {
      try {
        const data = await apiFetch(`${API_BASE}/api/booking/finalize`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        setSessionToken(data.session_jwt_token);
        setStep(1); // Move to guide after finalize succeeds
      } catch {
        setError(true);
      }
    };

    finalize();
  }, [token]);

  // Polling function
  const startPolling = async () => {
    if (!sessionToken) return;
    
    setIsPolling(true);
    setStep(3); // Show generating screen
    
    const poll = async () => {
      try {
        abortControllerRef.current = new AbortController();
        
        const response = await fetch(`${API_BASE}/api/provisioning-status`, {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
          },
          signal: abortControllerRef.current.signal,
        });
        
        if (!response.ok) {
          throw new Error("Polling failed");
        }
        
        const data = await response.json();
        
        if (data.status === 'ready' && data.access_code) {
          setAccessCode(String(data.access_code));
          setStep(4);
          setIsPolling(false);
        } else if (data.status === 'failed') {
          setError(true);
          setIsPolling(false);
        } else if (data.status === 'pending') {
          // Continue polling
          pollingRef.current = window.setTimeout(poll, 1000);
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return; // Cancelled, don't set error
        }
        setError(true);
        setIsPolling(false);
      }
    };
    
    poll();
  };

  const handleTermsAccept = () => {
    startPolling();
  };

  const nextStep = () => setStep(s => Math.min(s + 1, 4));
  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  // Show error if no token or API failed
  if (error) {
    return (
      <div className="h-[100dvh] bg-[#f9f8f5] flex flex-col overflow-hidden">
        <div className="flex-shrink-0 flex items-center justify-center h-14 border-b border-[#153F36]/5 bg-[#FDFBF7]">
          <span className="font-funnel font-semibold text-2xl text-primary">qwip</span>
        </div>
        <ErrorBox />
        <footer className="flex-shrink-0 py-3 text-center bg-[#f9f8f5]">
          <span className="text-sm text-muted-foreground">© 2025 qwip. All rights reserved.</span>
        </footer>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] bg-[#f9f8f5] flex flex-col overflow-hidden">
      {/* Top Bar */}
      <div className="flex-shrink-0 flex items-center justify-center h-14 border-b border-[#153F36]/5 bg-[#FDFBF7]">
        <span className="font-funnel font-semibold text-2xl text-primary">qwip</span>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col w-full overflow-hidden">
        <AnimatePresence mode="wait">
          {step === 0 && <FinalizingPayment key="finalizing" />}
          {step === 1 && <OnboardingGuide key="guide" onComplete={nextStep} onBack={() => {}} />}
          {step === 2 && <TermsAndConditions key="terms" onComplete={handleTermsAccept} onBack={prevStep} isLoading={isPolling} />}
          {step === 3 && <GeneratingAccessCode key="generating" />}
          {step === 4 && accessCode && sessionToken && <AccessCodeReveal key="code" accessCode={accessCode} sessionToken={sessionToken} />}
        </AnimatePresence>
      </div>
      
      {/* Footer */}
      <footer className="flex-shrink-0 py-3 text-center bg-[#f9f8f5]">
        <span className="text-sm text-muted-foreground">© 2025 qwip. All rights reserved.</span>
      </footer>
    </div>
  );
}
